import { useEffect, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { Spinner } from '@/components/ui/spinner'
import { api } from '@/api/client'

/**
 * AuthCallbackPage handles the OAuth2 redirect from the API server.
 *
 * After a user authenticates via an OAuth provider, the API redirects
 * the browser to /auth/callback?token=... (or ?error=...).
 * This page stores the JWT token and does a full-page redirect to "/".
 * On the fresh page load, AuthProvider's init picks up the token from
 * localStorage and loads the user normally - this avoids all React
 * state-timing races between setUser, context propagation, and routing.
 */
export function AuthCallbackPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const token = searchParams.get('token')
    const errorParam = searchParams.get('error')
    const message = searchParams.get('message')

    if (errorParam) {
      setError(message || errorParam)
      return
    }

    if (token) {
      api.setToken(token)
      // Full-page redirect - AuthProvider init will load the user
      // from the token already in localStorage on the fresh load.
      window.location.replace('/')
    } else {
      setError('No authentication token received. Please try signing in again.')
    }
  }, [searchParams])

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <div className="w-full max-w-sm text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-destructive/10 text-destructive">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="15" y1="9" x2="9" y2="15" />
              <line x1="9" y1="9" x2="15" y2="15" />
            </svg>
          </div>
          <h1 className="text-lg font-semibold">Authentication Failed</h1>
          <p className="mt-2 text-sm text-muted-foreground">{error}</p>
          <button
            onClick={() => navigate('/login', { replace: true })}
            className="mt-4 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Back to Sign In
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-4">
        <Spinner className="h-8 w-8 text-primary" />
        <p className="text-sm text-muted-foreground">Signing you in…</p>
      </div>
    </div>
  )
}
